import type { MoveResourceType, ProjectModel } from '@xver/apis/v1/projects/model';
import { MoveResourceItem, MoveResourcesModal } from '@xver/components/Organisms';
import { canOfferMove, focusAfterRender, resourceKey } from '@xver/utils/projects';
import { useRef, useState } from 'react';
import { useActiveProject } from './useActiveProject';
import { useProjectsQuery } from './useProjectsQuery';

/**
 * Spread on the menu button (a row's, or a detail page's "More actions") so focus returns to it
 * when the move dialog closes.
 */
export const moveTriggerProps = (type: MoveResourceType, id: string | number) => ({
    'data-move-trigger': resourceKey(type, id),
});

/**
 * Call when a dialog opened from a menu (a row's, or a detail page's "More actions") closes, since
 * Mantine would return focus to the unmounted menu item. Focuses that menu's `moveTriggerProps`
 * button, or `#main-content` when the button is gone or disabled; pass `rowRemoved` when the
 * dialog's action deletes the row.
 */
export const focusRowMenu = (type: MoveResourceType, id: string | number, rowRemoved = false) =>
    focusAfterRender(() => {
        const trigger = rowRemoved
            ? null
            : document.querySelector<HTMLButtonElement>(
                  `[data-move-trigger="${resourceKey(type, id)}"]`
              );
        return trigger && !trigger.disabled ? trigger : document.getElementById('main-content');
    });

/**
 * "Move to project…" for resource rows of one `type` in the active project. `canMove` follows
 * `canOfferMove`, so an owner with no other project still gets it and creates the target in the
 * dialog; render `moveModal` once per table.
 * The source is the active project when `requestMove` runs, so a detail page offers the move only
 * while its resource is in the active project. Pass `triggerStaysAfterMove` on a detail page,
 * whose menu button is still there after a move, so focus returns to it instead of `#main-content`.
 */
export function useMoveResource(
    type: MoveResourceType,
    { triggerStaysAfterMove = false }: { triggerStaysAfterMove?: boolean } = {}
) {
    const { project } = useActiveProject();
    const { data: projects } = useProjectsQuery();
    const [opened, setOpened] = useState(false);
    const [items, setItems] = useState<MoveResourceItem[]>([]);
    const [source, setSource] = useState<ProjectModel>();
    const returnFocusRef = useRef<HTMLElement | null>(null);
    const movedRef = useRef(false);

    const canMove = !!project && canOfferMove(projects ?? [], project, type);

    const requestMove = (item: Omit<MoveResourceItem, 'type'>) => {
        returnFocusRef.current = document.querySelector<HTMLElement>(
            `[data-move-trigger="${resourceKey(type, item.id)}"]`
        );
        movedRef.current = false;
        setSource(project);
        setItems([{ ...item, type }]);
        setOpened(true);
    };

    const moveSource = source ?? project;
    const moveModal = moveSource ? (
        <MoveResourcesModal
            opened={opened}
            onClose={() => {
                setOpened(false);
                const trigger =
                    movedRef.current && !triggerStaysAfterMove ? null : returnFocusRef.current;
                focusAfterRender(() =>
                    trigger?.isConnected ? trigger : document.getElementById('main-content')
                );
            }}
            onMoved={(results) => {
                movedRef.current = results.some((result) => result.status === 'moved');
            }}
            source={moveSource}
            resources={items}
        />
    ) : null;

    return { canMove, requestMove, moveModal };
}
